/**
 * Lying snow: how deep it is where something stands, and what it does to
 * them. The pure half of the snow; `three/snowField.ts` draws it.
 *
 * - **Plowed.** Roads and paths are cleared, the snow thrown up in a bank
 *   along their edges; while it snows they take a dusting that makes them
 *   slippery.
 * - **Trodden.** Feet and tyres press the snow down, about to a sixth of its
 *   depth, and leave their prints; new snow fills them in.
 * - **Wading.** Deep snow is heavy going on foot: knee-deep halves the pace.
 * - **Ploughing.** A vehicle's tyres lose grip in snow, and once the snow is
 *   near its ground clearance its body ploughs it too: the drag grows until
 *   it is more than the engine can pull, and it is stuck. A lorry, with its
 *   clearance, goes on through what stops a car; a dirt bike's knobblies
 *   cope with a little.
 */

import { LANDCOVER } from '../sources/shortbread.js';

/**
 * Ground the map paints that is plowed as the roads are: car parks, and
 * paved areas (squares, bicycle parking). The snow keeps off it, and so does
 * its drag (see `three/snowField.ts` and the terrain's snow tint).
 */
export const PLOWED_LANDCOVER: readonly number[] = [LANDCOVER.parking, LANDCOVER.paving];

/** How the ground drives, as `three/Vehicle.ts` and `three/Motorbike.ts` take it. */
export interface DriveSurface {
  /** Grip, a share of dry tarmac's. */
  grip: number;
  /** Rolling resistance, m/s². */
  rolling: number;
  /** Top speed, a share. */
  topSpeed: number;
}

export type SnowBuild = 'car' | 'truck' | 'moto';

/** Ground clearance, metres: the snow depth at which the body starts to plough. */
export const CLEARANCE: Record<SnowBuild, number> = { car: 0.16, truck: 0.32, moto: 0.26 };

/** What a trodden or driven track leaves of the snow: packed down to this share of its depth. */
export const PACKED = 0.15;

/** Below this, the snow is a white dusting on the ground, nothing to wade or plough. */
export const DUSTING = 0.02;

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/**
 * The snow lying at a spot, metres: the day's depth, less what was cleared
 * (`cleared` 0..1, a road plowed) and what was pressed down (`pressed` 0..1,
 * a footprint or a tyre track).
 */
export function lyingDepth(depth: number, cleared: number, pressed: number): number {
  return Math.max(0, depth) * (1 - clamp01(cleared)) * (1 - (1 - PACKED) * clamp01(pressed));
}

/**
 * The ground under a vehicle in snow: `lying` metres of it under its wheels
 * (`vehicleSnow`, the plowed ground already counted out), or where it lies
 * on a road (`onRoad`) the slush and dusting while it snows (`snowing`).
 * Rolling resistance grows with the square of the snow's depth over the
 * clearance, past what any engine pulls when the snow is above the body.
 */
export function snowSurface(base: DriveSurface, lying: number, build: SnowBuild, onRoad: boolean, snowing: boolean): DriveSurface {
  if (lying < DUSTING) return onRoad && snowing ? { ...base, grip: base.grip * 0.7 } : base;
  const r = lying / CLEARANCE[build];
  // Tyres on snow: a third to a half of tarmac's grip, less as it deepens.
  const grip = base.grip * Math.max(0.3, (build === 'moto' ? 0.62 : 0.5) - 0.25 * Math.min(1, r));
  // Churning through: gentle up to most of the clearance, then the body
  // starts to plough, past a car's pull at about 1.2 times its clearance
  // (some 20 cm). m/s², on a car's scale (a truck's chassis scales its own).
  const plough = (1.0 * r + 14 * Math.max(0, r - 0.7) ** 2) * (build === 'moto' ? 0.75 : 1);
  return { grip, rolling: base.rolling + 0.3 + plough, topSpeed: base.topSpeed * Math.max(0.05, 1 - 0.75 * Math.min(1, r)) };
}

/** Whether `lying` snow is past what `build` can drive on into. */
export function stuckIn(lying: number, build: SnowBuild): boolean {
  return lying > CLEARANCE[build] * 1.2;
}

/**
 * The snow at one wheel, metres, plowed ground counted as none: where it
 * stands (what the body sits over), and a little ahead of it and behind it
 * (what it drives into either way), with how packed each of those is (0..1).
 */
export interface WheelSnow {
  here: number;
  ahead: number;
  pressedAhead: number;
  behind: number;
  pressedBehind: number;
}

/**
 * The snow a vehicle drives into and backs into, from each of its wheels.
 *
 * Driving on, it is the snow under all the wheels together: front wheels in
 * a drift with the back ones on the road is half the drag of the whole car
 * in it. Backing up, each wheel rolls back into what is behind it — the
 * ruts it made, the road it came off — so a car that only nosed into a drift
 * backs out. But where the body sits on the snow (deeper than one and a half
 * times the clearance) it has to be shoved back off it, and the more of the
 * car is in that deep, the more of that there is: a car buried all round is
 * stuck both ways.
 */
export function vehicleSnow(wheels: readonly WheelSnow[], build: SnowBuild): { ahead: number; behind: number } {
  if (wheels.length === 0) return { ahead: 0, behind: 0 };
  const clearance = CLEARANCE[build];
  let ahead = 0, behind = 0, resting = 0, covered = 0;
  for (const w of wheels) {
    ahead += lyingDepth(w.ahead, 0, w.pressedAhead);
    behind += lyingDepth(w.behind, 0, w.pressedBehind);
    resting += Math.max(0, w.here - 1.5 * clearance);
    if (w.here > clearance) covered++;
  }
  const n = wheels.length;
  return { ahead: ahead / n, behind: behind / n + (covered / n) * (resting / n) * 2 };
}

/**
 * Walking pace in `lying` snow, a share of the pace on clear ground: ankle
 * deep slows a little, knee deep (half a metre) to about half, waist deep
 * to a slow wade.
 */
export function wading(lying: number): number {
  if (lying < DUSTING) return 1;
  return Math.max(0.22, 1 / (1 + (lying / 0.45) ** 1.6));
}

/**
 * Where a foot comes down: `side` −1 left, 1 right, a hand's width either
 * side of the line walked along `heading` (scene radians, 0 toward −z, as
 * the player's yaw).
 */
export function footAt(x: number, z: number, yaw: number, side: -1 | 1): { x: number; z: number } {
  // Forward is (−sin yaw, −cos yaw); right of it is (cos yaw, −sin yaw).
  const off = 0.11 * side;
  return { x: x + Math.cos(yaw) * off, z: z - Math.sin(yaw) * off };
}

/** A vehicle's wheels, as the snow takes their tracks: across and along from the middle, and tyre width. */
export interface WheelLayout {
  /** Half the track, metres. */
  halfTrack: number;
  /** Axles, metres along from the middle (forward positive). */
  axles: readonly number[];
  tyre: number;
}

export const WHEELS: Record<SnowBuild, WheelLayout> = {
  car: { halfTrack: 0.76, axles: [1.34, -1.34], tyre: 0.22 },
  truck: { halfTrack: 1.0, axles: [2.4, -1.6, -2.9], tyre: 0.32 },
  moto: { halfTrack: 0, axles: [0.72, -0.72], tyre: 0.12 },
};

/** Where `layout`'s wheels touch the ground, the vehicle at (x, z) facing `yaw`. */
export function wheelsAt(x: number, z: number, yaw: number, layout: WheelLayout, wheelbase?: number): Array<{ x: number; z: number }> {
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  const rx = Math.cos(yaw), rz = -Math.sin(yaw);
  const scale = wheelbase === undefined || layout.axles.length < 2 ? 1 : wheelbase / (layout.axles[0] - layout.axles[layout.axles.length - 1]);
  const out: Array<{ x: number; z: number }> = [];
  for (const a of layout.axles) {
    const along = a * scale;
    const sides = layout.halfTrack === 0 ? [0] : [-layout.halfTrack, layout.halfTrack];
    for (const s of sides) out.push({ x: x + fx * along + rx * s, z: z + fz * along + rz * s });
  }
  return out;
}

/**
 * How fast new snow fills tracks in: a track packed full (`pressed` 1) is
 * level again after this many centimetres of fresh snow.
 */
export const FILLS_IN_CM = 6;

/** What a tyre is doing in the snow, for how much of it it throws. */
export interface SprayInput {
  /** Road speed, m/s. */
  speed: number;
  /** Sideways slide, m/s. */
  slip: number;
  /** Loose snow where the tyre rolls, metres (fresh, not packed). */
  loose: number;
  /** Wheelspin, 0 gripping .. 1 spinning flat out in place. */
  spin: number;
  /** A plowed road's dusting of new snow, 0..1. */
  dust: number;
}

/**
 * Snow thrown up by one tyre, lumps a second: a fan off the tread with
 * speed, a rooster tail spinning in place, a sideways sheet in a slide. A
 * few centimetres of loose snow throw as much as it gets; a road's dusting
 * a little.
 */
export function sprayRate(t: SprayInput): number {
  const snow = clamp01(t.loose / 0.08) + 0.35 * clamp01(t.dust);
  if (snow <= 0) return 0;
  const rolling = Math.min(1, Math.abs(t.speed) / 14);
  const sliding = clamp01((t.slip - 1) / 4);
  return Math.min(1, snow) * (28 * rolling + 70 * clamp01(t.spin) + 30 * sliding);
}
